/**
 * What the keys move through (docs/ux.md §12): the units that can still act, a shooter's
 * targets, the cell cursor. Pure; the turn machine and the hotkeys hook use it.
 */
import { parseCellId, fireTargets, toCellId, type GameState } from '@brig/rules-engine';
import { shipsToMove, squadronCandidates, unitsToFire } from './turn.ts';

/** Reading order on the map: row by row, left to right. */
function byCell(state: GameState) {
  const cellOf = (id: string) =>
    state.ships.find((s) => s.id === id)?.cell ?? state.forts.find((f) => f.id === id)?.cell ?? '';
  return (a: string, b: string) => {
    const pa = parseCellId(cellOf(a));
    const pb = parseCellId(cellOf(b));
    if (!pa || !pb) return a < b ? -1 : 1;
    return pa.y - pb.y || pa.x - pb.x || (a < b ? -1 : 1);
  };
}

/**
 * U-KEY-02: the own units Tab cycles through. Mustering: ships that may sail and units that
 * can fire; in battle: ships still to move and units that can fire.
 */
export function actingUnits(state: GameState, mustering: boolean): string[] {
  const ids = new Set([
    ...(mustering ? squadronCandidates(state) : shipsToMove(state)),
    ...unitsToFire(state),
  ]);
  return [...ids].sort(byCell(state));
}

/** The next (or previous) of `ids` after `current`, wrapping; null when there are none. */
export function cycle(ids: readonly string[], current: string | null, dir: 1 | -1): string | null {
  if (ids.length === 0) return null;
  const i = current === null ? -1 : ids.indexOf(current);
  if (i === -1) return dir === 1 ? (ids[0] ?? null) : (ids.at(-1) ?? null);
  return ids[(i + dir + ids.length) % ids.length] ?? null;
}

/** F (U-FIRE-03): the shooter's targets in reading order. */
export function targetsOf(state: GameState, shooter: string): string[] {
  return fireTargets(state, shooter).sort(byCell(state));
}

/** The cursor one step (dx, dy) from `cell`, kept on the map. */
export function stepCell(state: GameState, cell: string, dx: number, dy: number): string {
  const p = parseCellId(cell);
  if (!p) return cell;
  const x = Math.max(0, Math.min(state.map.width - 1, p.x + dx));
  const y = Math.max(0, Math.min(state.map.height - 1, p.y + dy));
  return toCellId({ x, y });
}
